// Registered playback providers. To add a new provider:
//   1. Implement a class with id / supports(request) / resolve(request)
//      (see VidkingProvider in providers.js for the shape).
//   2. Add it to the providers array below.
//   3. Set preferredPlaybackProvider in public/config.js to switch the default.
import { config } from "../config";
import {
  PlaybackProviderRegistry,
  HostedFileProvider,
  TorrentProvider,
  TorBoxProvider,
  YouTubeProvider,
  ReserveEmbedProvider,
  RESERVE_EMBED_HOSTS,
} from "./providers";

// VidLink (vidlink.pro / "vidking") and SuperEmbed are both OUT of the chain
// as of 2026-09-24, at the admin's explicit direction: the six reserve hosts
// are the backup tier instead.
//
// Their classes are deliberately still in providers.js and the /embed/*,
// /api/vidking/* proxy still exists, so restoring either is one line here —
// this repository is not under version control, so unwiring beats deleting.
//
// Worth remembering when judging this chain: SuperEmbed was measured as
// genuinely unplayable through the proxy (Cloudflare Turnstile gated it with a
// "failure_retry" loop) and VidLink was carrying every title the admin had not
// mapped to a hosted file, YouTube, TorBox or a torrent. The reserve hosts are
// therefore now the only remote fallback, and they are embedded directly
// (cross-origin) for that reason — see RESERVE_EMBED_HOSTS in providers.js.
//
// The reserve tier: six third-party TMDB-keyed embed hosts, served through the
// same-origin /embed/reserve/<key>/... proxy which strips their ads and blocks
// their ad hosts (see web/reserve-embeds.js and playback-proxy.js).
//
// Built from config.reserveEmbeds when present, so the LIST AND ITS ORDER can
// be changed by editing public/config.js on the host — no rebuild. That is the
// point: these clone domains rotate constantly (vidking.net's nameservers died
// in Sep 2026; vidsrc has moved .to → .xyz → .net → .me → .sbs), so retiring
// one and promoting another must not require shipping code. Keys must exist in
// RESERVE_EMBED_HOSTS and in the server allowlist; unknown keys are ignored.
const reserveHosts = Array.isArray(config.reserveEmbeds)
  ? config.reserveEmbeds
      .map((key) => RESERVE_EMBED_HOSTS.find((h) => h.key === String(key).toLowerCase()))
      .filter(Boolean)
  : RESERVE_EMBED_HOSTS;

// Priority: admin-mapped hosted files (the URL the admin pastes wins first;
// LuluStream/Playmate per the link's host; falls through when unmapped/dead)
// -> admin-mapped YouTube (official uploads; falls through instantly when
// unmapped or the video died) -> TorBox cloud (when the admin configured it)
// -> local torrent swarm -> the six reserve embeds, in config order.
// Each returns UNAVAILABLE when it can't serve, and the registry falls through
// to the next — the reserve tier is genuinely last, so it only ever plays when
// the sources we control (and SuperEmbed) have nothing. Beyond resolution-time
// fallback, a runtime failure posts poku:provider-dead with the provider's own
// id, and PlayerScreen re-resolves with exclude:[that id] — so a dead reserve
// host walks to the next reserve host instead of showing Try Again. See
// PlayerScreen.
const registry = new PlaybackProviderRegistry(
  [
    new HostedFileProvider(),
    new YouTubeProvider(),
    new TorBoxProvider(),
    new TorrentProvider(),
    // VidLinkProvider and SuperEmbedProvider were here — removed 2026-09-24
    // (see the note above).
    ...reserveHosts.map((host) => new ReserveEmbedProvider(host)),
  ],
  "hosted",
);

export function resolvePlayback(request, options) {
  return registry.resolve(request, options);
}
